import type { SxProps, Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import { brandPrimary, brandPrimaryHover, brandPrimaryMuted } from "./tokens";

/** Primary brand color for inputs, selects, loaders (aligned with Tailwind `primary`). */
export const formPrimary = brandPrimary;
export const formPrimaryHover = brandPrimaryHover;
export const formPrimaryMuted = brandPrimaryMuted;

/** End adornment icon chip (date/time pickers, location, etc.). */
export const formFieldEndIconWrapSx: SystemStyleObject<Theme> = {
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	cursor: "pointer",
	backgroundColor: formPrimaryMuted,
	borderRadius: "6px",
	padding: "6px",
	transition: "all 0.2s ease",
	"&:hover": {
		backgroundColor: "rgba(42, 150, 153, 0.18)",
		transform: "scale(1.05)",
	},
};

export const formFieldEndIconSx: SystemStyleObject<Theme> = {
	color: formPrimary,
	fontSize: "1.3rem",
};

export const formRadioSx: SystemStyleObject<Theme> = {
	color: formPrimary,
	"&.Mui-checked": { color: formPrimary },
};

/** Shared control height (dropdowns + text inputs). */
export const formControlHeight = 48;

/** Auth forms on short viewports — matches `.auth-form-compact` in index.css */
export const AUTH_FORM_COMPACT_CONTROL_HEIGHT = 42;

/** Menu item states — used by MUI theme + Select styles. */
export const formMenuItem = {
	hoverBg: brandPrimaryMuted,
	hoverColor: "#1e293b",
	selectedBg: brandPrimary,
	selectedColor: "#ffffff",
	selectedHoverBg: brandPrimaryHover,
} as const;

/** Canonical field validation color — matches admin/institute helper text. */
export const formFieldErrorColor = "#d32f2f";

const borderError = formFieldErrorColor;

/** App form labels (external, above control). */
export const formFieldLabelClassName =
	"mb-2 block font-medium text-sm sm:text-base text-foreground";

export const formFieldRequiredMarkClassName = "text-red-500";

/** Custom helper text below selects / non-MUI fields. */
export const formFieldHelperTextClassName =
	"mt-1 font-['Poppins',sans-serif] text-[11px] leading-none";

export const formFieldHelperTextErrorClassName = "text-[#d32f2f]";

export const formFieldHelperTextMutedClassName = "text-gray-500";

export const formFieldErrorClassName = formFieldHelperTextClassName + " " + formFieldHelperTextErrorClassName;

export const getFormFieldHelperTextClassName = (error?: boolean) =>
	`${formFieldHelperTextClassName} ${error ? formFieldHelperTextErrorClassName : formFieldHelperTextMutedClassName}`;

export const formFieldPlaceholderColor = "#9CA3AF";
export const formFieldSelectedColor = "#111827";
export const formSelectPlaceholderClassName = "tvet-select-placeholder";
export const formSelectValueClassName = "tvet-select-value";

/** Truncate long selected labels inside select fields (flex child needs minWidth: 0). */
export const formSelectValueTruncateSx: SystemStyleObject<Theme> = {
	display: "block",
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
	minWidth: 0,
	width: "100%",
	maxWidth: "100%",
	flex: "1 1 0%",
};

const placeholderColor = formFieldPlaceholderColor;

export const formFieldBg = "#FFF";
export const formFieldBorder = "#D9D9D9";
export const formFieldBorderWidth = 1.2;
const fieldBorderHover = "#C4C4C4";

export const selectChevron = {
	width: 35,
	height: 36,
	borderRadius: 4,
	rightInset: 5,
} as const;

export const selectChevronCompact = {
	width: 30,
	height: 30,
	borderRadius: 4,
	rightInset: 4,
} as const;

export interface SelectChevronSpec {
	width: number;
	height: number;
	borderRadius: number;
	rightInset: number;
}

export const getSelectChevron = (controlHeight = formControlHeight): SelectChevronSpec =>
	controlHeight <= 44 ? selectChevronCompact : selectChevron;

export const getChevronReserve = (controlHeight = formControlHeight): number => {
	const chevron = getSelectChevron(controlHeight);
	return chevron.width + chevron.rightInset + 8;
};

export const selectChevronCapClassName =
	"absolute z-[2] flex cursor-pointer items-center justify-center border-0 bg-primary p-0 outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

const fieldBorderSx = (error?: boolean): SystemStyleObject<Theme> => ({
	borderColor: error ? borderError : formFieldBorder,
	borderWidth: error ? 1.2 : formFieldBorderWidth,
});

const outlinedFieldBorderStates = (error?: boolean): SystemStyleObject<Theme> => ({
	"& fieldset": fieldBorderSx(error),
	"& .MuiOutlinedInput-notchedOutline": fieldBorderSx(error),
	"&:hover fieldset": {
		borderColor: error ? borderError : fieldBorderHover,
	},
	"&:hover .MuiOutlinedInput-notchedOutline": {
		borderColor: error ? borderError : fieldBorderHover,
	},
	"&.Mui-focused fieldset": fieldBorderSx(error),
	"&.Mui-focused .MuiOutlinedInput-notchedOutline": fieldBorderSx(error),
	"&.Mui-error fieldset": {
		borderColor: `${borderError} !important`,
	},
	"&.Mui-error .MuiOutlinedInput-notchedOutline": {
		borderColor: `${borderError} !important`,
	},
});

export const formOutlinedFieldShellSx = (error?: boolean): SystemStyleObject<Theme> => ({
	backgroundColor: `${formFieldBg} !important`,
	borderRadius: "10px",
	...outlinedFieldBorderStates(error),
});

export const outlinedInputExternalLabelProps = {
	notched: false,
} as const;

const selectOutlineNoNotchSx = (error?: boolean): SystemStyleObject<Theme> => ({
	"& fieldset": {
		top: 0,
		...fieldBorderSx(error),
	},
	"& .MuiOutlinedInput-notchedOutline": {
		top: 0,
		...fieldBorderSx(error),
		"& legend": {
			display: "none",
			maxWidth: 0.01,
		},
	},
});

export const selectOutlinedInputSx = (error?: boolean, height = formControlHeight): SystemStyleObject<Theme> => ({
	...formOutlinedFieldShellSx(error),
	...selectOutlineNoNotchSx(error),
	minHeight: height,
	height,
	width: "100%",
	maxWidth: "100%",
	minWidth: 0,
	alignItems: "center",
	paddingRight: `${getChevronReserve(height)}px !important`,
	overflow: "hidden",
	cursor: "pointer",
});

export const textOutlinedInputSx = (error?: boolean, height = formControlHeight): SystemStyleObject<Theme> => ({
	...formOutlinedFieldShellSx(error),
	minHeight: height,
	height,
});

const selectInnerSx = (height = formControlHeight): SystemStyleObject<Theme> => {
	const isCompact = height <= 44;

	return {
		padding: isCompact ? "9px 7px" : "13px 8px 13px 15px",
		fontSize: isCompact ? "13px" : "14px",
		fontWeight: 400,
		lineHeight: isCompact ? 1.25 : 1.5,
		display: "flex",
		alignItems: "center",
		fontFamily: "Poppins, sans-serif",
		minHeight: isCompact ? "unset" : height,
		height: isCompact ? "100%" : height,
		width: "100%",
		flex: "1 1 0%",
		minWidth: 0,
		maxWidth: "100%",
		overflow: "hidden",
		boxSizing: "border-box",
		color: formFieldPlaceholderColor,
		backgroundColor: "transparent",
		cursor: "pointer",
		[`& .${formSelectPlaceholderClassName}, & .${formSelectValueClassName}`]: formSelectValueTruncateSx,
	};
};

export const selectControlSx = (error?: boolean, height = formControlHeight): SxProps<Theme> => {
	const outlined = selectOutlinedInputSx(error, height);
	return {
		width: "100%",
		maxWidth: "100%",
		minWidth: 0,
		"&.MuiOutlinedInput-root": outlined,
		"& .MuiOutlinedInput-root": outlined,
		"& .MuiSelect-select": selectInnerSx(height),
		"& .MuiSelect-icon": {
			display: "none",
		},
		"& .MuiSelect-select.Mui-disabled": {
			WebkitTextFillColor: "#111827",
			color: "#111827",
			opacity: 1,
		},
		"& .MuiSelect-select.Mui-disabled span": {
			color: `${formFieldSelectedColor} !important`,
		},
		[`& .MuiSelect-select .${formSelectPlaceholderClassName}`]: {
			color: `${formFieldPlaceholderColor} !important`,
			WebkitTextFillColor: `${formFieldPlaceholderColor} !important`,
		},
		[`& .MuiSelect-select .${formSelectValueClassName}`]: {
			color: `${formFieldSelectedColor} !important`,
			WebkitTextFillColor: `${formFieldSelectedColor} !important`,
		},
	};
};

const textInputInnerBase: SystemStyleObject<Theme> = {
	fontFamily: "Poppins, sans-serif",
	fontSize: "14px",
	fontWeight: 400,
	color: "#111827",
	boxSizing: "border-box",
	backgroundColor: "transparent",
};

export type TextFieldControlSize = number | { height?: string; padding?: string };

const resolveCustomFieldHeight = (height?: string): number => {
	if (!height) return formControlHeight;
	const parsed = parseInt(String(height), 10);
	if (!Number.isFinite(parsed) || parsed < 32) return formControlHeight;
	return parsed;
};

export const textFieldControlSx = (error?: boolean, size?: TextFieldControlSize): SxProps<Theme> => {
	if (typeof size === "object" && size !== null) {
		const height = resolveCustomFieldHeight(size.height);
		return {
			"& .MuiOutlinedInput-root": textOutlinedInputSx(error, height),
			"& input, & textarea": {
				...textInputInnerBase,
				padding: size.padding ?? "13px 15px",
				height,
				minHeight: height,
			},
			"& input::placeholder, & textarea::placeholder": {
				color: placeholderColor,
				opacity: 1,
			},
		};
	}

	const height = typeof size === "number" ? size : formControlHeight;

	return {
		"& .MuiOutlinedInput-root": textOutlinedInputSx(error, height),
		"& input, & textarea": {
			...textInputInnerBase,
			padding: "13px 15px",
			height,
		},
		"& input::placeholder, & textarea::placeholder": {
			color: placeholderColor,
			opacity: 1,
		},
	};
};

export const mergeSelectSx = (
	error?: boolean,
	...overrides: (SxProps<Theme> | undefined)[]
): SxProps<Theme> => [selectControlSx(error), ...overrides.filter(Boolean)] as SxProps<Theme>;

/** Toolbar / list filter selects (Employees, Invited, Shortlisted, table filters). */
export const filterSelectSx = {
	fontFamily: "Poppins, sans-serif",
	backgroundColor: "white",
	borderRadius: "8px",
	"& .MuiOutlinedInput-notchedOutline": {
		borderColor: formFieldBorder,
		borderWidth: `${formFieldBorderWidth}px`,
	},
	"&:hover .MuiOutlinedInput-notchedOutline": {
		borderColor: "var(--tvet-primary, #2a9699)",
	},
	"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
		borderColor: "var(--tvet-primary, #2a9699)",
		borderWidth: "1px",
	},
} as const;

export const filterMenuItemSx = {
	fontFamily: "Poppins, sans-serif",
	"&:hover": {
		backgroundColor: "var(--tvet-secondary, #e1f0f5)",
	},
	"&.Mui-selected": {
		backgroundColor: "var(--tvet-primary, #2a9699)",
		borderBottom: "none",
		"&:hover": {
			backgroundColor: "var(--tvet-primary-hover, #248a8c)",
		},
	},
} as const;

/** Slight dim behind open select menus (default MUI backdrop is invisible). */
export const selectMenuBackdropProps = {
	invisible: false,
	sx: {
		backgroundColor: "rgba(15, 23, 42, 0.1)",
	},
} as const;

export const defaultSelectMenuProps = {
	BackdropProps: selectMenuBackdropProps,
	PaperProps: {
		sx: {
			borderRadius: 2,
			boxShadow: "0 2px 8px 0 rgba(16, 24, 40, 0.04)",
			mt: 1,
		},
	},
	sx: {
		zIndex: 10001,
	},
} as const;

/** Toolbar filter dropdown menus. */
export const filterMenuProps = {
	...defaultSelectMenuProps,
	PaperProps: {
		sx: {
			borderRadius: "8px",
			boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
			mt: 1,
			maxHeight: 300,
		},
	},
} as const;

export { placeholderColor };
